/**
 * Represents a time value in seconds, with utility methods for conversion.
 * Extends `Number` so TypeScript treats it as a number in arithmetic operations.
 */
class TimeValue extends Number {
  private readonly seconds: number;

  /**
   * Creates a new TimeValue instance.
   * @param seconds - The number of seconds.
   */
  constructor(seconds: number) {
    super(seconds);
    this.seconds = seconds;
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /**
   * Gets the time value in seconds.
   */
  get value(): number {
    return this.seconds;
  }

  /**
   * Returns the time value in seconds.
   */
  toSeconds(): number {
    return this.seconds;
  }

  /**
   * Returns the time value in milliseconds.
   */
  toMilliseconds(): number {
    return this.seconds * 1000;
  }

  /**
   * Ensures arithmetic operations work as expected.
   */
  valueOf(): number {
    return this.seconds;
  }

  /**
   * Converts to string representation.
   */
  toString(): string {
    return String(this.seconds);
  }

  /**
   * Handles implicit type conversion.
   */
  [Symbol.toPrimitive](hint: string): number | string {
    return hint === "number" ? this.seconds : String(this.seconds);
  }

  /**
   * Ensures `JSON.stringify()` and `console.log()` output the numeric value.
   */
  toJSON(): number {
    return this.seconds;
  }
}

type TimeValueNumber = TimeValue & number;

/**
 * Provides utility methods to convert time units into seconds.
 */
export class TimeUtils {
  /**
   * Creates a TimeValue instance that behaves like both a `number` and `TimeValue`.
   */
  private static createTimeValue(seconds: number): TimeValueNumber {
    return new TimeValue(seconds) as TimeValueNumber;
  }

  static seconds(seconds: number) {
    return TimeUtils.createTimeValue(seconds);
  }

  static minutes(minutes: number) {
    return TimeUtils.createTimeValue(minutes * 60);
  }

  static hours(hours: number) {
    return TimeUtils.createTimeValue(hours * 60 * 60);
  }

  static days(days: number) {
    return TimeUtils.createTimeValue(days * 24 * 60 * 60);
  }

  static weeks(weeks: number) {
    return TimeUtils.createTimeValue(weeks * 7 * 24 * 60 * 60);
  }

  static months(months: number) {
    return TimeUtils.createTimeValue(months * 30.44 * 24 * 60 * 60);
  }

  static years(years: number) {
    return TimeUtils.createTimeValue(years * 365.25 * 24 * 60 * 60);
  }
}
